<script setup>
// NOTICE: KAPTION-INTEGRATION
// embedded-channel.DASHBOARD.5 / OPTIN.1 / OPTIN.2 — on a plain web widget,
// administrators (flag on) can enable Embedded; on an Embedded inbox they can
// turn it back into a plain web widget from a danger zone. Both ask first.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import { KaptionEmbeddedAPI } from 'dashboard/api/kaptionEmbedded';
import {
  EMBEDDED_FLAG,
  isEmbeddedInbox,
  isPlainWebWidgetInbox,
} from 'dashboard/helper/kaptionEmbedded';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({ inbox: { type: Object, required: true } });

const { t } = useI18n();
const store = useStore();
const { isAdmin } = useAdmin();
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();

const dialog = ref(null);
const busy = ref(false);

const available = computed(
  () => isAdmin.value && isKaptionFlagEnabled(EMBEDDED_FLAG)
);
const canEnable = computed(
  () => available.value && isPlainWebWidgetInbox(props.inbox)
);
// Only an inbox that was opted in (origin opt_in) can go back to a plain
// widget; one created as Embedded (origin created) cannot. Without `origin`
// in the payload, every Embedded inbox offers it.
const canDisable = computed(() => {
  if (!available.value || !isEmbeddedInbox(props.inbox)) return false;
  const origin = props.inbox.embedded?.origin;
  return !origin || origin === 'opt_in';
});

const copy = computed(() => {
  const name = props.inbox.name;
  if (canDisable.value) {
    return {
      title: t('INBOX_MGMT.EMBEDDED.OPTIN.DISABLE_TITLE'),
      description: t('INBOX_MGMT.EMBEDDED.OPTIN.DISABLE_DESC'),
      button: t('INBOX_MGMT.EMBEDDED.OPTIN.DISABLE_BUTTON'),
      confirmTitle: t('INBOX_MGMT.EMBEDDED.OPTIN.DISABLE_CONFIRM_TITLE', {
        name,
      }),
      confirmDescription: t('INBOX_MGMT.EMBEDDED.OPTIN.DISABLE_CONFIRM_DESC'),
    };
  }
  return {
    title: t('INBOX_MGMT.EMBEDDED.OPTIN.ENABLE_TITLE'),
    description: t('INBOX_MGMT.EMBEDDED.OPTIN.ENABLE_DESC'),
    button: t('INBOX_MGMT.EMBEDDED.OPTIN.ENABLE_BUTTON'),
    confirmTitle: t('INBOX_MGMT.EMBEDDED.OPTIN.ENABLE_CONFIRM_TITLE', { name }),
    confirmDescription: t('INBOX_MGMT.EMBEDDED.OPTIN.ENABLE_CONFIRM_DESC'),
  };
});

const ask = () => dialog.value?.open();

const confirm = async () => {
  const disabling = canDisable.value;
  const accountId = store.getters.getCurrentAccountId;
  busy.value = true;
  try {
    if (disabling) {
      await KaptionEmbeddedAPI.disable(accountId, props.inbox.id);
    } else {
      await KaptionEmbeddedAPI.enable(accountId, props.inbox.id);
    }
    // Refresh the inbox payload so `embedded` (and the tabs) follow.
    await store.dispatch('inboxes/get');
    useAlert(
      disabling
        ? t('INBOX_MGMT.EMBEDDED.OPTIN.DISABLED')
        : t('INBOX_MGMT.EMBEDDED.OPTIN.ENABLED')
    );
  } catch (error) {
    const data = error?.response?.data;
    useAlert(
      data?.error ||
        data?.message ||
        (disabling
          ? t('INBOX_MGMT.EMBEDDED.OPTIN.DISABLE_ERROR')
          : t('INBOX_MGMT.EMBEDDED.OPTIN.ENABLE_ERROR'))
    );
  } finally {
    busy.value = false;
    dialog.value?.close();
  }
};
</script>

<template>
  <SettingsSection
    v-if="canEnable || canDisable"
    :title="canDisable ? t('INBOX_MGMT.EMBEDDED.OPTIN.DANGER_ZONE') : ''"
    :show-border="false"
    data-testid="embedded-optin"
  >
    <div
      class="flex flex-wrap items-center justify-between gap-4 p-4 border rounded-lg"
      :class="canDisable ? 'border-n-ruby-6' : 'border-n-weak bg-n-solid-1'"
    >
      <div class="flex flex-col min-w-0 gap-1 flex-1">
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          {{ copy.title }}
        </p>
        <p class="mb-0 text-sm text-n-slate-11">{{ copy.description }}</p>
      </div>
      <NextButton
        :outline="canDisable"
        :ruby="canDisable"
        :label="copy.button"
        data-testid="embedded-optin-button"
        @click="ask"
      />
    </div>
    <Dialog
      ref="dialog"
      :type="canDisable ? 'alert' : 'edit'"
      :title="copy.confirmTitle"
      :description="copy.confirmDescription"
      :confirm-button-label="copy.button"
      :is-loading="busy"
      @confirm="confirm"
    />
  </SettingsSection>
</template>
